// `remember`: the agent's own hand on harness memory. When a query fails
// and the agent works out the fix, it states the general rule here so the
// next chat starts from it. Rate-limited per run so a chatty model cannot
// flood the store, and stored at agent-source confidence, below what a
// user-stated lesson gets. The per-run count is read from the ledger's
// undistilled events, which agent_end clears, so a long chat gets a fresh
// allowance every run rather than three for its whole life.

import { Type } from "@earendil-works/pi-ai";
import { MAX_REMEMBER_PER_RUN } from "./harnessMemoryRun.js";

const rememberSchema = Type.Object({
    rule: Type.String({
        description:
            "One sentence stating a general rule about how the analytics API must be called: what to do, " +
            "naming the entity, field, operator, or window rule when it matters. Not a fact about the data.",
    }),
    entity: Type.Optional(Type.String({ description: "ENTITY_TYPE_ name the rule is specific to. Omit for a rule that applies everywhere." })),
    fields: Type.Optional(Type.Array(Type.String(), { description: "DIMENSION_FIELD_ names the rule depends on." })),
});

export function createRememberTool({ store, ledger = null, chatId = null }) {
    let usedWithoutLedger = 0;
    const usedThisRun = () => (ledger?.pending
        ? ledger.pending().filter((e) => e.kind === "remember").length
        : usedWithoutLedger);
    return {
        name: "remember",
        label: "Remember a lesson",
        description:
            "Record a durable lesson about the analytics API for future chats: a query shape that fails and " +
            "what to do instead. Use it once you have SEEN a query fail and then succeed after a change, and " +
            "state the rule generally. Do not record facts about the data, guesses, or restated error text. " +
            "Lessons show up in later chats' prompts and fade unless they keep proving useful.",
        parameters: rememberSchema,
        async execute(_toolCallId, { rule, entity, fields }) {
            if (usedThisRun() >= MAX_REMEMBER_PER_RUN) {
                return { content: [{ type: "text", text: JSON.stringify({ stored: false, reason: `at most ${MAX_REMEMBER_PER_RUN} lessons per run` }) }] };
            }
            const entry = store.remember({
                rule,
                entity: entity || null,
                fields: Array.isArray(fields) ? fields : [],
                source: "agent",
                evidence: { chatId },
            });
            if (!entry) {
                return { content: [{ type: "text", text: JSON.stringify({ stored: false, reason: "empty rule" }) }] };
            }
            if (ledger?.record) ledger.record({ kind: "remember", tool: "remember", chatId, entryId: entry.id, shape: null });
            else usedWithoutLedger++;
            return {
                content: [{ type: "text", text: JSON.stringify({ stored: true, id: entry.id, confidence: entry.confidence }) }],
                details: { id: entry.id, rule: entry.rule },
            };
        },
    };
}
